import { useEffect } from 'react';

function isCustomEvent<SomeEvent extends CustomEvent>(
    event:Event,
    test?:((event:CustomEvent) => event is SomeEvent) | nullish,
): event is SomeEvent {
    return 'detail' in event && 'initCustomEvent' in event && (!test || test(event as CustomEvent));
}

const READY_OPTIMIZELY_LISTENER: Record<string, boolean> = {};
// Returns 0 if the listener was ready, otherwise 1 and we will retry in 100ms
const checkListener = (eventName:string, details:unknown) => {
    if (READY_OPTIMIZELY_LISTENER[eventName]) {
        window.dispatchEvent(new CustomEvent(eventName, { detail: details }));
        return 0;
    }
    setTimeout(() => checkListener(eventName, details), 100);
    return 1;
};
window.OptimizelyEvents = window.OptimizelyEvents || [];
while (window.OptimizelyEvents.length) {
    const { eventName, details } = window.OptimizelyEvents.pop()!;
    checkListener(eventName, details);
}
window.OptimizelyEvents.push = ({ eventName, details }) => checkListener(eventName, details);

/*
 * This function sets up listeners for Optimizely events. If sent correctly from the Optimizely test,
 * the callback function will be called even if sent before the listener is set up.
 *
 * **NOTE:** This is exported for non-React A/B tests. Use `useOptimizelyVariant` for React components hooks.
 *
 * @param optimizelyEvent The name of the Optimizely A/B test. Used for setting up the listener
 * @param callback The callback function to be called when the event is fired
 * @param test An optional type guarding function to confirm the event is valid before executing the callback
 */
export function setUpOptimizelyListener<SomeEvent extends CustomEvent>(
    optimizelyEvent:string,
    callback:(event:SomeEvent) => unknown,
    test?:((event:CustomEvent) => event is SomeEvent) | nullish,
) {
    function eventListener(event: Event) {
        if (isCustomEvent<SomeEvent>(event, test)) {
            callback(event);
        }
    }
    window.addEventListener(optimizelyEvent, eventListener);
    READY_OPTIMIZELY_LISTENER[optimizelyEvent] = true;
    // Return event in case we need to remove the listener
    return eventListener;
}

/*
 * This hook is used to listen to Optimizely events. It will automatically clear up listeners on rerenders.
 *
 * **NOTE:** It is important that `deps` covers all the dependencies used in the callback function.
 * To ensure this, confirm `useEffect(callback, deps)` passed the react-hooks/exhaustive-deps lint rule.
 *
 * @param optimizelyEvent The name of the Optimizely A/B test. Used for setting up the listener
 * @param callback The callback function to be called when the event is fired
 * @param test An optional type guarding function to confirm the event is valid before executing the callback
 * @param deps An optional array of dependencies used in the callback function
 */
export function useOptimizelyVariant<SomeEvent extends CustomEvent>(
    optimizelyEvent:string,
    callback:(event:SomeEvent) => unknown,
    test?:((event:CustomEvent) => event is SomeEvent) | nullish,
    deps:unknown[] = [],
) {
    useEffect(() => {
        const listenerFun = setUpOptimizelyListener<SomeEvent>(optimizelyEvent, callback, test);
        return () => {
            window.removeEventListener(optimizelyEvent, listenerFun);
            READY_OPTIMIZELY_LISTENER[optimizelyEvent] = false;
        };
        // We expect the caller to pass in the unknown dependencies used in the callback and need to be included.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, callback, optimizelyEvent, test]);
}
